import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import ShareModal from '@/components/ShareModal';
import ComparisonDetailModal from '@/components/ComparisonDetailModal';
import DownloadFormatModal from '@/components/DownloadFormatModal';
import { isAdminTester, logAdminBypass } from '@/utils/adminBypass';
import { 
  Image, 
  Wand2, 
  Sparkles, 
  Camera, 
  Palette,
  Download,
  Share2,
  Trash2,
  Eye,
  Filter,
  Grid,
  List,
  Loader2,
  Columns,
  Expand,
  Edit,
  ZoomIn,
  MoreVertical,
  ExternalLink,
  Layers,
  Zap
} from 'lucide-react';

const GalleryPage = () => {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedCreation, setSelectedCreation] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  // Comparison detail modal state
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  
  // Download modal state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadImageUrl, setDownloadImageUrl] = useState(null);
  
  // Enhancement/Upscale loading state
  const [processingId, setProcessingId] = useState(null);

  const typeConfig = {
    'image_to_prompt': { label: 'Prompts', icon: <Image className="w-4 h-4" />, color: 'bg-blue-500/10 text-blue-500' },
    'prompt_enhance': { label: 'Enhanced', icon: <Wand2 className="w-4 h-4" />, color: 'bg-purple-500/10 text-purple-500' },
    'image_generate': { label: 'Images', icon: <Sparkles className="w-4 h-4" />, color: 'bg-amber-500/10 text-amber-500' },
    'mockup_generate': { label: 'Mockups', icon: <Camera className="w-4 h-4" />, color: 'bg-green-500/10 text-green-500' },
    'background_generate': { label: 'Backgrounds', icon: <Palette className="w-4 h-4" />, color: 'bg-pink-500/10 text-pink-500' },
    'comparison': { label: 'Comparisons', icon: <Columns className="w-4 h-4" />, color: 'bg-indigo-500/10 text-indigo-500' },
    'upscale': { label: 'Upscaled', icon: <ZoomIn className="w-4 h-4" />, color: 'bg-cyan-500/10 text-cyan-500' },
    'variations': { label: 'Variations', icon: <Layers className="w-4 h-4" />, color: 'bg-orange-500/10 text-orange-500' },
    'make_it_better': { label: 'Improved', icon: <Zap className="w-4 h-4" />, color: 'bg-violet-500/10 text-violet-500' },
    'refine_image': { label: 'Refined', icon: <Wand2 className="w-4 h-4" />, color: 'bg-rose-500/10 text-rose-500' },
  };

  useEffect(() => {
    fetchGallery(true);
    fetchStats();
  }, [filter]);

  const fetchGallery = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }

    try {
      const newOffset = reset ? 0 : offset;
      const response = await api.get(`/gallery?limit=12&offset=${newOffset}&type_filter=${filter}`);
      
      if (reset) {
        setCreations(response.data.creations);
      } else {
        setCreations(prev => [...prev, ...response.data.creations]);
      }
      
      setTotal(response.data.total);
      setHasMore(response.data.has_more);
      setOffset(newOffset + 12);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/gallery/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleDelete = async (creationId) => {
    setDeletingId(creationId);
    try {
      await api.delete(`/creations/${creationId}`);
      setCreations(prev => prev.filter(c => c.id !== creationId));
      setTotal(prev => prev - 1);
      toast.success('Creation deleted');
      fetchStats();
    } catch (error) {
      toast.error('This action failed. No credits were used.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = (creation) => {
    const imageBase64 = creation.output_data?.image_base64;
    if (!imageBase64) {
      toast.error('No image available to download');
      return;
    }
    
    // Use format modal for consistent download experience
    setDownloadImageUrl(`data:image/png;base64,${imageBase64}`);
    setDownloadModalOpen(true);
  };

  const handleShare = (creation) => {
    setSelectedCreation(creation);
    setShowShareModal(true);
  };

  // Open in Editor - navigates to ImageGenerator with pre-loaded image and prompt
  const handleOpenInEditor = (creation) => {
    const imageBase64 = creation.output_data?.image_base64;
    const prompt = creation.input_data?.prompt || '';
    const style = creation.input_data?.style || 'product';
    
    // Track action
    trackGalleryAction('gallery_edit_again_click', creation.id);
    
    // Store in sessionStorage for target page to pick up
    sessionStorage.setItem('editorImage', imageBase64 ? `data:image/png;base64,${imageBase64}` : '');
    sessionStorage.setItem('editorPrompt', prompt);
    sessionStorage.setItem('editorStyle', style);
    
    // Navigate based on creation type
    if (creation.type === 'background_generate') {
      navigate('/dashboard/background?fromGallery=true');
    } else if (creation.type === 'mockup_generate') {
      sessionStorage.setItem('mockupImage', imageBase64 ? `data:image/png;base64,${imageBase64}` : '');
      navigate('/dashboard/mockup?fromGallery=true');
    } else {
      navigate('/dashboard/generate?fromGallery=true');
    }
    toast.success('Opening in Editor...');
  };

  // Create Mockup from gallery image - navigates to MockupGenerator with pre-loaded image
  const handleCreateMockup = (creation) => {
    const imageBase64 = creation.output_data?.image_base64;
    if (!imageBase64) {
      toast.error('No image available for mockup');
      return;
    }
    
    // Track action
    trackGalleryAction('gallery_create_mockup_click', creation.id);
    
    // Store in sessionStorage for MockupGenerator to pick up
    sessionStorage.setItem('mockupImage', `data:image/png;base64,${imageBase64}`);
    sessionStorage.setItem('mockupFromGallery', 'true');
    
    navigate('/dashboard/mockup?fromGallery=true');
    toast.success('Opening Mockup Generator...');
  };

  // Enhance image from gallery
  const handleEnhance = async (creation) => {
    const imageBase64 = creation.output_data?.image_base64;
    if (!imageBase64) {
      toast.error('No image available to enhance');
      return;
    }
    
    const isAdmin = isAdminTester(user);
    logAdminBypass('Gallery', 'handleEnhance', user, isAdmin);
    
    // Phase 1: Free unlimited - no credit check
    
    setProcessingId(creation.id);
    try {
      const response = await api.post('/ai/enhance-image', {
        image_url: `data:image/png;base64,${imageBase64}`
      });
      
      if (response.data.enhanced_url) {
        toast.success('Image enhanced! Opening download...');
        setDownloadImageUrl(response.data.enhanced_url);
        setDownloadModalOpen(true);
        if (!isAdmin) {
          await refreshUser();
        }
      }
    } catch (error) {
      toast.error('Enhancement failed. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  // Upscale image from gallery
  const handleUpscale = async (creation, scale) => {
    const imageBase64 = creation.output_data?.image_base64;
    if (!imageBase64) {
      toast.error('No image available to upscale');
      return;
    }
    
    const cost = scale === 2 ? 2 : 4;
    const isAdmin = isAdminTester(user);
    logAdminBypass('Gallery', 'handleUpscale', user, isAdmin);
    
    // Phase 1: Free unlimited - no credit check
    
    setProcessingId(creation.id);
    try {
      const response = await api.post('/ai/upscale-image', {
        image_url: `data:image/png;base64,${imageBase64}`,
        scale
      });
      
      if (response.data.upscaled_url) {
        toast.success(`Image upscaled ${scale}x! Opening download...`);
        setDownloadImageUrl(response.data.upscaled_url);
        setDownloadModalOpen(true);
        if (!isAdmin) {
          await refreshUser();
        }
      }
    } catch (error) {
      toast.error('Upscale failed. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  // Generate variations from gallery
  const handleGenerateVariations = async (creation) => {
    const prompt = creation.input_data?.prompt;
    const style = creation.input_data?.style || 'product';
    
    if (!prompt) {
      toast.error('No prompt available for this image');
      return;
    }
    
    const variationCost = 6;
    const isAdmin = isAdminTester(user);
    logAdminBypass('Gallery', 'handleGenerateVariations', user, isAdmin);
    
    // Phase 1: Free unlimited - no credit check
    
    // Navigate to Image Generator with the prompt to generate variations there
    sessionStorage.setItem('editorPrompt', prompt);
    sessionStorage.setItem('editorStyle', style);
    navigate('/dashboard/generate?fromGallery=true&action=variations');
    toast.info('Opening editor to generate variations...');
  };

  // Make It Better from gallery (now uses image-to-image refinement)
  const handleMakeItBetter = async (creation) => {
    const imageBase64 = creation.output_data?.image_base64;
    const prompt = creation.input_data?.prompt;
    const style = creation.input_data?.style || 'product';
    
    // Track action
    trackGalleryAction('gallery_make_it_better_click', creation.id);
    
    if (!prompt) {
      toast.error('No prompt available for this image');
      return;
    }
    
    const cost = 5;
    const isAdmin = isAdminTester(user);
    logAdminBypass('Gallery', 'handleMakeItBetter', user, isAdmin);
    
    // Phase 1: Free unlimited - no credit check
    
    setProcessingId(creation.id);
    try {
      const response = await api.post('/ai/make-it-better', {
        original_prompt: prompt,
        style: style,
        // Pass reference image for true image-to-image refinement
        reference_image_base64: imageBase64 || null,
        preserve_composition: true,
        refinement_strength: 0.45
      });
      
      if (response.data.success) {
        // Use image_base64 if available, otherwise image_url
        const newImageUrl = response.data.image_base64 
          ? `data:image/png;base64,${response.data.image_base64}`
          : response.data.image_url;
        
        const refinementUsed = response.data.refinement_used;
        toast.success(refinementUsed ? 'Image refined! Structure preserved.' : 'Image improved!');
        setDownloadImageUrl(newImageUrl);
        setDownloadModalOpen(true);
        if (!isAdmin) {
          await refreshUser();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Make It Better failed. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  // Refine Image - dedicated refinement with style options
  const handleRefineImage = async (creation, style = 'product') => {
    const imageBase64 = creation.output_data?.image_base64;
    
    if (!imageBase64) {
      toast.error('No image available to refine');
      return;
    }
    
    const cost = 5;
    const isAdmin = isAdminTester(user);
    logAdminBypass('Gallery', 'handleRefineImage', user, isAdmin);
    
    // Phase 1: Free unlimited - no credit check
    
    setProcessingId(creation.id);
    try {
      const response = await api.post('/ai/refine-image', {
        image_base64: imageBase64,
        style: style,
        strength: 0.45,
        improve_lighting: true,
        improve_detail: true,
        improve_color: true
      });
      
      if (response.data.success) {
        const newImageUrl = response.data.image_base64 
          ? `data:image/png;base64,${response.data.image_base64}`
          : response.data.image_url;
        
        toast.success('Image refined! Structure preserved.');
        setDownloadImageUrl(newImageUrl);
        setDownloadModalOpen(true);
        if (!isAdmin) {
          await refreshUser();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refine failed. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const getCreationPreview = (creation) => {
    // Check for image_base64 in output_data
    const imageBase64 = creation.output_data?.image_base64;
    if (imageBase64) {
      return `data:image/png;base64,${imageBase64}`;
    }
    
    // Check for image_url in output_data (some older creations might have URLs)
    const imageUrl = creation.output_data?.image_url || creation.output_data?.upscaled_url;
    if (imageUrl && imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // Check for variations - show first variation image
    const variations = creation.output_data?.variations;
    if (Array.isArray(variations) && variations.length > 0) {
      const firstVariation = variations[0];
      if (typeof firstVariation === 'string' && firstVariation.length > 100) {
        // Likely base64
        return `data:image/png;base64,${firstVariation}`;
      } else if (firstVariation?.image_base64) {
        return `data:image/png;base64,${firstVariation.image_base64}`;
      }
    }
    
    return null;
  };

  // Check if creation is text-only (no image preview expected)
  const isTextOnlyCreation = (creation) => {
    return ['prompt_enhance', 'image_to_prompt'].includes(creation.type);
  };

  // Check if creation has a valid image to display
  const hasValidImage = (creation) => {
    // Text-only creations are valid but show text instead of image
    if (isTextOnlyCreation(creation)) return true;
    
    // Check for image_base64
    if (creation.output_data?.image_base64) return true;
    
    // Check for image_url
    if (creation.output_data?.image_url) return true;
    
    // Check for upscaled_url
    if (creation.output_data?.upscaled_url) return true;
    
    // Check for variations array with images
    const variations = creation.output_data?.variations;
    if (Array.isArray(variations) && variations.length > 0) {
      const first = variations[0];
      if (typeof first === 'string' && first.length > 100) return true;
      if (first?.image_base64) return true;
    }
    
    // Comparisons have their own display
    if (creation.type === 'comparison') return true;
    
    return false;
  };

  // Filter creations to only show valid ones (no empty cards)
  const validCreations = creations.filter(hasValidImage);

  // Track gallery action (reuse existing analytics)
  const trackGalleryAction = async (action, creationId) => {
    try {
      await api.post('/analytics/track', {
        event: action,
        properties: { creation_id: creationId }
      });
    } catch (e) {
      // Silent fail - analytics should not block UI
      console.log('[Gallery] Analytics track failed:', action);
    }
  };

  // Get text content for text-only creations
  const getTextContent = (creation) => {
    if (creation.type === 'prompt_enhance') {
      return creation.output_data?.enhanced || creation.input_data?.prompt || 'Enhanced prompt';
    }
    if (creation.type === 'image_to_prompt') {
      return creation.output_data?.prompt || 'Extracted prompt';
    }
    return null;
  };

  const getCreationTitle = (creation) => {
    // For comparisons, show the original prompt
    if (creation.type === 'comparison') {
      const prompt = creation.input_data?.original_prompt;
      if (prompt) {
        return prompt.substring(0, 50) + (prompt.length > 50 ? '...' : '');
      }
      return 'AI Comparison';
    }
    
    if (creation.input_data?.prompt) {
      return creation.input_data.prompt.substring(0, 50) + (creation.input_data.prompt.length > 50 ? '...' : '');
    }
    if (creation.output_data?.enhanced) {
      return creation.output_data.enhanced.substring(0, 50) + '...';
    }
    return typeConfig[creation.type]?.label || 'Creation';
  };

  // Get comparison summary for display
  const getComparisonSummary = (creation) => {
    if (creation.type !== 'comparison') return null;
    const models = creation.output_data?.models_used || [];
    return `${models.length} model${models.length !== 1 ? 's' : ''} compared`;
  };

  // Handle opening comparison detail modal
  const handleExpandComparison = (creation) => {
    setSelectedComparison(creation);
    setShowComparisonModal(true);
  };

  // Handle deleting a comparison from the modal
  const handleDeleteComparison = async (creationId) => {
    await handleDelete(creationId);
  };

  return (
    <div className="space-y-8 fade-in" data-testid="gallery-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 
            className="text-2xl font-bold mb-1"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            My Gallery
          </h1>
          <p className="text-muted-foreground">
            {total} creation{total !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="image_generate">Images</SelectItem>
              <SelectItem value="mockup_generate">Mockups</SelectItem>
              <SelectItem value="background_generate">Backgrounds</SelectItem>
              <SelectItem value="make_it_better">Improved</SelectItem>
              <SelectItem value="refine_image">Refined</SelectItem>
              <SelectItem value="upscale">Upscaled</SelectItem>
              <SelectItem value="variations">Variations</SelectItem>
              <SelectItem value="prompt_enhance">Enhanced Prompts</SelectItem>
              <SelectItem value="image_to_prompt">Extracted Prompts</SelectItem>
              <SelectItem value="comparison">Comparisons</SelectItem>
            </SelectContent>
          </Select>

          {/* View Mode Toggle */}
          <div className="flex border border-border rounded-lg">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-btn"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          {Object.entries(typeConfig).map(([type, config]) => (
            <Card key={type} className="bg-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                  {config.icon}
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_type?.[type] || 0}</p>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Gallery Grid/List */}
      {loading ? (
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" 
          : "space-y-4"
        }>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className={viewMode === 'grid' ? "h-64 rounded-xl" : "h-24 rounded-xl"} />
          ))}
        </div>
      ) : validCreations.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No creations yet
            </h3>
            <p className="text-sm text-muted-foreground">
              Start creating to fill your gallery
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {validCreations.map((creation, index) => {
            const preview = getCreationPreview(creation);
            const config = typeConfig[creation.type] || {};
            const isTextOnly = isTextOnlyCreation(creation);
            const textContent = isTextOnly ? getTextContent(creation) : null;
            
            return (
              <motion.div
                key={creation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden group" data-testid={`gallery-item-${creation.id}`}>
                  {/* Image Preview or Comparison Preview or Text Preview */}
                  <div 
                    className={`aspect-square bg-muted/30 relative ${creation.type === 'comparison' ? 'cursor-pointer' : ''}`}
                    onClick={creation.type === 'comparison' ? () => handleExpandComparison(creation) : undefined}
                  >
                    {preview ? (
                      <img 
                        src={preview}
                        alt={getCreationTitle(creation)}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback on image load error
                          e.target.style.display = 'none';
                          e.target.nextSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    
                    {/* Fallback display when no preview OR image fails to load */}
                    {!preview && !isTextOnly && creation.type !== 'comparison' && (
                      <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${config.color || 'bg-muted'}`}>
                          {config.icon || <Sparkles className="w-6 h-6 text-muted-foreground" />}
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">
                          {config.label || creation.type}
                        </p>
                        <p className="text-xs text-muted-foreground/60 mt-1">
                          {creation.type === 'upscale' && `${creation.scale || 2}x Upscale`}
                          {creation.type === 'variations' && 'Variations'}
                        </p>
                      </div>
                    )}
                    
                    {/* Text-only creation preview */}
                    {isTextOnly && (
                      <div className="w-full h-full flex flex-col p-4 bg-gradient-to-br from-muted/50 to-muted/20">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${config.color || 'bg-muted'}`}>
                          {config.icon || <Wand2 className="w-5 h-5" />}
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {creation.type === 'prompt_enhance' ? 'Enhanced Prompt' : 'Extracted Prompt'}
                        </p>
                        <p className="text-sm line-clamp-5 flex-1 overflow-hidden">
                          {textContent}
                        </p>
                      </div>
                    )}
                    
                    {creation.type === 'comparison' && !preview && (
                      // Special comparison preview - clickable
                      <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center group-hover:bg-muted/50 transition-colors duration-150">
                        <div className="w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-105 transition-transform duration-150">
                          <Columns className="w-6 h-6 text-indigo-500" />
                        </div>
                        <p className="text-sm font-medium mb-1">Compare Results</p>
                        <p className="text-xs text-muted-foreground">
                          {getComparisonSummary(creation)}
                        </p>
                        {creation.output_data?.models_used && (
                          <div className="flex flex-wrap gap-1 mt-2 justify-center">
                            {creation.output_data.models_used.slice(0, 4).map(modelId => (
                              <span key={modelId} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                                {modelId.toUpperCase()}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Expand hint on hover */}
                        <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          <span className="text-xs text-primary flex items-center gap-1">
                            <Expand className="w-3 h-3" />
                            Click to expand
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Overlay Actions - different for comparison vs others */}
                    <div className={`absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4 ${creation.type === 'comparison' ? 'pointer-events-none' : ''}`}>
                      {creation.type === 'comparison' ? (
                        // Comparison-specific overlay - just show expand button
                        <Button 
                          size="sm" 
                          variant="secondary" 
                          className="pointer-events-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandComparison(creation);
                          }}
                        >
                          <Expand className="w-4 h-4 mr-1.5" />
                          View Details
                        </Button>
                      ) : (
                        // Regular creation overlay - REUSE/ENGAGEMENT ACTIONS
                        <>
                          {/* Primary row: Edit Again + Make It Better */}
                          <div className="flex gap-2">
                            {/* Edit Again - navigates back to generator */}
                            {preview && (creation.type === 'image_generate' || creation.type === 'mockup_generate' || creation.type === 'background_generate') && (
                              <Button 
                                size="sm" 
                                onClick={() => handleOpenInEditor(creation)}
                                data-testid={`edit-again-${creation.id}`}
                                className="bg-white/90 text-gray-800 hover:bg-white"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit Again
                              </Button>
                            )}
                            
                            {/* Make It Better - prominent action */}
                            {preview && creation.input_data?.prompt && (
                              <Button 
                                size="sm"
                                onClick={() => handleMakeItBetter(creation)}
                                disabled={processingId === creation.id}
                                data-testid={`make-better-${creation.id}`}
                                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                              >
                                <Zap className="w-4 h-4 mr-1" />
                                Make It Better
                              </Button>
                            )}
                          </div>
                          
                          {/* Secondary row: Create Mockup + More */}
                          <div className="flex gap-2 mt-2">
                            {/* Create Mockup - visible action */}
                            {preview && creation.type === 'image_generate' && (
                              <Button 
                                size="sm" 
                                variant="secondary"
                                onClick={() => handleCreateMockup(creation)}
                                data-testid={`create-mockup-${creation.id}`}
                              >
                                <Camera className="w-4 h-4 mr-1" />
                                Create Mockup
                              </Button>
                            )}
                            
                            {/* Actions dropdown for more options */}
                            {preview && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="secondary">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleDownload(creation)}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Download
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleShare(creation)}>
                                    <Share2 className="w-4 h-4 mr-2" />
                                    Share
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleEnhance(creation)}
                                    disabled={processingId === creation.id}
                                  >
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Enhance (1 cr)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleUpscale(creation, 2)}
                                    disabled={processingId === creation.id}
                                  >
                                    <ZoomIn className="w-4 h-4 mr-2" />
                                    Upscale 2x (2 cr)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleUpscale(creation, 4)}
                                    disabled={processingId === creation.id}
                                  >
                                    <ZoomIn className="w-4 h-4 mr-2" />
                                    Upscale 4x (4 cr)
                                  </DropdownMenuItem>
                                  {/* Variations - only for image creations with prompts */}
                                  {creation.input_data?.prompt && (
                                    <DropdownMenuItem 
                                      onClick={() => handleGenerateVariations(creation)}
                                      disabled={processingId === creation.id}
                                    >
                                      <Layers className="w-4 h-4 mr-2" />
                                      Generate Variations (6 cr)
                                    </DropdownMenuItem>
                                  )}
                                  {/* Refine Image - dedicated refinement without prompt */}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleRefineImage(creation, 'product')}
                                    disabled={processingId === creation.id}
                                    className="text-primary"
                                  >
                                    <Wand2 className="w-4 h-4 mr-2" />
                                    Refine (Product Style)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleRefineImage(creation, 'illustration')}
                                    disabled={processingId === creation.id}
                                  >
                                    <Wand2 className="w-4 h-4 mr-2" />
                                    Refine (Illustration)
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleRefineImage(creation, 'sticker')}
                                    disabled={processingId === creation.id}
                                  >
                                    <Wand2 className="w-4 h-4 mr-2" />
                                    Refine (Sticker)
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem 
                                      className="text-destructive"
                                      onSelect={(e) => e.preventDefault()}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Creation?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction 
                                        onClick={() => handleDelete(creation.id)}
                                        className="bg-destructive text-destructive-foreground"
                                      >
                                        {deletingId === creation.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          </div>
                          
                          {/* Processing indicator */}
                          {processingId === creation.id && (
                            <div className="absolute inset-0 bg-background/90 flex items-center justify-center">
                              <Loader2 className="w-6 h-6 animate-spin text-primary" />
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Share badge */}
                    {creation.share_info?.shared && (
                      <div className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm px-2 py-1 rounded-full text-xs flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {creation.share_info.views}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${config.color || 'bg-muted'}`}>
                        {config.label || creation.type}
                      </span>
                    </div>
                    <p className="text-sm mt-2 line-clamp-1">
                      {getCreationTitle(creation)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(creation.created_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="space-y-3">
          {validCreations.map((creation, index) => {
            const preview = getCreationPreview(creation);
            const config = typeConfig[creation.type] || {};
            const isTextOnly = isTextOnlyCreation(creation);
            
            return (
              <motion.div
                key={creation.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <Card data-testid={`gallery-item-${creation.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {/* Thumbnail */}
                    <div className={`w-16 h-16 rounded-lg flex-shrink-0 overflow-hidden ${preview ? 'bg-muted/30' : config.color || 'bg-muted/30'}`}>
                      {preview ? (
                        <img 
                          src={preview} 
                          alt="" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {config.icon || <Sparkles className="w-6 h-6 text-muted-foreground" />}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${config.color || 'bg-muted'}`}>
                          {config.label}
                        </span>
                        {creation.share_info?.shared && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {creation.share_info.views}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium truncate">{getCreationTitle(creation)}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(creation.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {preview && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => handleDownload(creation)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleShare(creation)}>
                            <Share2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Creation?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDelete(creation.id)}
                              className="bg-destructive text-destructive-foreground"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {hasMore && !loading && (
        <div className="flex justify-center">
          <Button 
            variant="outline" 
            onClick={() => fetchGallery(false)}
            disabled={loadingMore}
            data-testid="load-more-btn"
          >
            {loadingMore ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}

      {/* Share Modal */}
      {selectedCreation && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedCreation(null);
          }}
          creationId={selectedCreation.id}
          imageBase64={selectedCreation.output_data?.image_base64}
          title={getCreationTitle(selectedCreation)}
          type={selectedCreation.type}
        />
      )}

      {/* Comparison Detail Modal */}
      <ComparisonDetailModal
        isOpen={showComparisonModal}
        onClose={() => {
          setShowComparisonModal(false);
          setSelectedComparison(null);
        }}
        comparison={selectedComparison}
        onDelete={handleDeleteComparison}
      />
      
      {/* Download Format Modal - Consistent with other pages */}
      <DownloadFormatModal
        isOpen={downloadModalOpen}
        onClose={() => {
          setDownloadModalOpen(false);
          setDownloadImageUrl(null);
        }}
        imageUrl={downloadImageUrl}
        defaultFilename="mockuppro-gallery"
      />
    </div>
  );
};

export default GalleryPage;
